import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import Button from "@mui/material/Button";
import { useDispatch } from "react-redux";
import { setLoading } from "slices/appSlice";
import AdoptionDecisionsModal from "./adoptionDecision/AdoptionDecisionsModal";
import Api from "api";
import EditHPQuarterandYearModal from "./adoptionDecision/EditHPQuarterandYearModal";
import { formErrorStrfromObj } from "utils/shared";
import dayjs from "dayjs";

const EditHPCreateAdoptionDecisions = ({
  hpKey,
  disabled,
  contractTypesSelection,
  initialContractTypesRef,
  contractTypes,
  onAdoptionDecisionsSaved,
}) => {
  const [newLogo, setNewLogo] = useState(false);
  const [initialImplementedKeys, setInitialImplementedKeys] = useState([]);
  const [newlyImplementedKeys, setNewlyImplementedKeys] = useState([]);
  const [policyTypeKeysforImplemented, setPolicyTypeKeysforImplemented] =
    useState([]);

  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);

  const [workflows, setWorkflows] = useState([]);
  const [yearOptions, setYearOptions] = useState([]);
  const [quarterOptions, setQuarterOptions] = useState([]);

  const [quarter, setQuarter] = useState("");
  const [year, setYear] = useState("");
  const [policyModalOpen, setPolicyModalOpen] = useState(false);
  const [filteredPolicies, setFilteredPolicies] = useState([]);
  const [allLineOfBusinessDetailsList, setAllLineOfBusinessDetailsList] =
    useState([]);
  const [adoptionOptions, setAdoptionOptions] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [basePolicies, setBasePolicies] = useState([]);

  const filteredBasePolicies = basePolicies.map((p) => ({
    ...p,
    healthPlanKey: hpKey,
  }));

  const openQandYModal = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = () => {
    setOpen(false);
    setFilteredPolicies([]);
  };

  const norm = (v) => (v || "").toLowerCase().trim();

  useEffect(() => {
    if (!contractTypesSelection || !initialContractTypesRef?.current) return;
    const initial = initialContractTypesRef?.current || {};
    setNewlyImplementedKeys(
      Object.entries(contractTypesSelection || {})
        .filter(
          ([key, val]) =>
            norm(val) === "implementation" &&
            norm(initial?.[key]) !== "implementation"
        )
        .map(([key]) => key)
    );
  }, [contractTypesSelection, initialContractTypesRef]);

  useEffect(() => {
    if (!initialContractTypesRef?.current) return;
    if (initialImplementedKeys.length > 0) return; // already set

    const keys = Object.entries(initialContractTypesRef.current)
      .filter(([_, val]) => norm(val) === "implementation")
      .map(([key]) => key);

    setInitialImplementedKeys(keys);
  }, [initialContractTypesRef?.current]);

  // Final implementedTypes (uppercase to match contractTypes.policyType like "GTM")
  const implementedTypes = useMemo(() => {
    let sourceKeys;
    if (newlyImplementedKeys.length > 0) {
      setNewLogo(true);
      sourceKeys = newlyImplementedKeys;
    } else {
      setNewLogo(false);
      sourceKeys = initialImplementedKeys;
    }
    return sourceKeys.map((k) => k.toUpperCase());
  }, [newlyImplementedKeys, initialImplementedKeys]);

  const getHealthPlanPolicies = useCallback(
    async (hpKey, selectedQuarter, selectedYear) => {
      if (!policyTypeKeysforImplemented.length) return;
      dispatch(setLoading(true));
      try {
        let response;
        if (newLogo) {
          response = await Api.healthPlanConfig.getAvalonBasePolicies({
            hpKey: hpKey,
            policyTypeKey: policyTypeKeysforImplemented,
            cabQuarter: selectedQuarter,
            cabYear: selectedYear,
          });
        } else {
          response = await Api.healthPlanConfig.getHPPolicies({
            hpKey: hpKey,
            policyTypeKey: policyTypeKeysforImplemented,
          });
        }
        setBasePolicies(response.data);
        return response.data;
      } catch (error) {
        dispatch(
          setDialog({ message: formErrorStrfromObj(error), title: "Error" })
        );
      } finally {
        dispatch(setLoading(false));
      }
    },
    [policyTypeKeysforImplemented, newLogo, dispatch]
  );

  const openPolicyModal = useCallback(async () => {
    try {
      const [lobsResponse, adoptOptionsResponse] = await Promise.all([
        Api.policyEnforcementGrid.getAllLineOfBusinessConfig(hpKey),
        Api.policyEnforcementGrid.getAllAdoptionOptions(),
      ]);
      setAllLineOfBusinessDetailsList(lobsResponse.data);
      setAdoptionOptions(adoptOptionsResponse.data);

      const policiesToUse = basePolicies.length
        ? filteredBasePolicies
        : await getHealthPlanPolicies(hpKey);

      if (policiesToUse && policiesToUse.length) {
        setFilteredPolicies(policiesToUse ?? []);
        const today = new Date();
        const year = today.getFullYear();
        const quarter = `Q${Math.floor(today.getMonth() / 3) + 1}`;
        setQuarter(quarter);
        setYear(year);
      } else {
        setDialog({
          message:
            "There are no policies available for Adoption Decisions at this time.",
        });
        return;
      }
      setPolicyModalOpen(true);
    } catch (error) {
      setPolicyModalOpen(false);
      setDialog({ message: error.message, title: "Error" });
    }
  }, [hpKey, basePolicies.length, getHealthPlanPolicies]);

  const handleQuarterYearSubmit = useCallback(
    async (selectedQuarter, selectedYear) => {
      // Update quarter and year in state
      setQuarter(selectedQuarter);
      setYear(selectedYear);

      const [lobsResponse, adoptOptionsResponse, filteredPolicies] =
        await Promise.all([
          Api.policyEnforcementGrid.getAllLineOfBusinessConfig(hpKey),
          Api.policyEnforcementGrid.getAllAdoptionOptions(),
          getHealthPlanPolicies(hpKey, selectedQuarter, selectedYear),
        ]);
      setAllLineOfBusinessDetailsList(lobsResponse.data);
      setAdoptionOptions(adoptOptionsResponse.data);

      if ((filteredPolicies?.length ?? 0) > 0) {
        setFilteredPolicies(filteredPolicies);
        setOpen(false);
        setPolicyModalOpen(true);
      } else {
        setDialog({
          message: "No policies available for selected Quarter and Year",
        });
      }
    },
    [hpKey, openPolicyModal]
  );

  const handleDialogClose = () => {
    setDialog(null);
  };

  useEffect(() => {
    if (!contractTypes.length || !implementedTypes.length) return;
    const keys = contractTypes
      .filter((contract) => implementedTypes.includes(contract.policyType))
      .map((c) => c.policyTypeKey);
    setPolicyTypeKeysforImplemented(keys);
  }, [contractTypes, implementedTypes]);

  useEffect(() => {
    if (policyTypeKeysforImplemented.length && newLogo) {
      const fetchWorkflows = async () => {
        try {
          const response =
            await Api.policyEnforcementGrid.getBulkActionWorkflow();
          setWorkflows(response.data);
          dispatch(setLoading(false));
        } catch (error) {
          alert("Error fetching workflows:", error);
          dispatch(setLoading(false));
        }
      };
      fetchWorkflows();
    }
  }, [policyTypeKeysforImplemented, newLogo]);

  const getDefaultYearOptions = () => {
    const currentYear = dayjs().year();
    return [
      (currentYear - 1).toString(), // 1 prior year
      currentYear.toString(), // current year
      (currentYear + 1).toString(), // next year
      (currentYear + 2).toString(), // 2nd year in the future
    ];
  };

  const DEFAULT_QUARTERS = ["Q1", "Q2", "Q3", "Q4"];
  const QUARTER_ORDER = { Q1: 1, Q2: 2, Q3: 3, Q4: 4 };

  useEffect(() => {
    // Find the CAB category object
    const selectedCategory = workflows.find(
      (workflow) => workflow.categoryOfWorkName === "CAB"
    );

    // Parse the comma-separated year and quarter values
    if (selectedCategory) {
      // Years
      const rawYears = selectedCategory?.yearText
        ? selectedCategory.yearText.split(",")
        : getDefaultYearOptions();

      const years = Array.from(
        new Set(
          rawYears
            .map((y) => (y != null ? String(y).trim() : ""))
            .filter(Boolean)
        )
      );

      //Quarters
      const rawQuarters = selectedCategory?.quarterAlias
        ? selectedCategory.quarterAlias.split(",")
        : DEFAULT_QUARTERS;

      const quarters = Array.from(
        new Set(rawQuarters.map((v) => String(v).trim()).filter(Boolean))
      ).sort((a, b) => (QUARTER_ORDER[a] ?? 99) - (QUARTER_ORDER[b] ?? 99));

      setYearOptions(years);
      setQuarterOptions(quarters);
    }
  }, [workflows]);

  return (
    <Box>
      {dialog && (
        <Dialog open onClose={handleDialogClose}>
          <DialogTitle>{dialog.title}</DialogTitle>
          <DialogContent>{dialog.message}</DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
      <Button
        disabled={hpKey == 999 || disabled}
        onClick={newLogo ? openQandYModal : openPolicyModal}
      >
        Enter Adoption Decisions
      </Button>

      {/** First Modal **/}
      {open && (
        <EditHPQuarterandYearModal
          handleClose={handleClose}
          gridData={filteredBasePolicies}
          quarterOptions={quarterOptions}
          yearOptions={yearOptions}
          onSubmit={handleQuarterYearSubmit}
        />
      )}

      {/** Second Modal **/}
      {policyModalOpen && (
        <AdoptionDecisionsModal
          hpKey={hpKey}
          quarter={quarter}
          year={year}
          filteredPolicies={filteredPolicies}
          adoptionOptions={adoptionOptions}
          allLineOfBusinessDetailsList={allLineOfBusinessDetailsList}
          handleClose={() => setPolicyModalOpen(false)}
          onSuccess={({ message, savedPolicyKeys }) => {
            // Remove saved policies from basePolicies so they don’t show again
            setBasePolicies((prev) =>
              prev.filter((p) => !savedPolicyKeys?.includes(p.policyKey))
            );
            setPolicyModalOpen(false); // close child first
            setDialog({ title: "Success", message: message });
            onAdoptionDecisionsSaved();
          }}
          actionType={"CAB"}
          isBreakFix={true}
        />
      )}
    </Box>
  );
};

// Memoize the component
// const MemoizedEditHPCreateAdoptionDecisions = memo(EditHPCreateAdoptionDecisions);
// EditHPCreateAdoptionDecisions.displayName = "EditHPCreateAdoptionDecisions";

// export default EditHPCreateAdoptionDecisions;

export default EditHPCreateAdoptionDecisions;
